import * as THREE from "three";
import { Cell, CellKind, LevelSim, SimEventKind, headingOffset } from "@highmarch/domain";
import { GroundMesh } from "../groundMesh";
import { WornSkins } from "../skins/wornSkins";
import type { PooledVfx } from "../vfxPool";
import { lib } from "./common";
import { PetAbilityFx } from "./petAbilityFx";
import { PetBark } from "./petBark";
import { EnemyViews } from "./enemies";
import { PetBody } from "./petBody";
import { PetFight } from "./petFight";
import { PetFoes } from "./petFoes";
import { Gait, PET_MOVE, naturalSpeed, turnToward } from "./petGait";
import { PetHop } from "./petHop";
import { PetMarker } from "./petMarker";
import { BARK_PEAKS, PetAct, PetMind, faceYaw, keepHides, petSpot, towerHides, withinReach } from "./petMind";
import { petLook, spotReach } from "./petPresent";
import { PET_STRIKE_TUNING, Pace, gaitOf, paceOf, strikeOf } from "./petStrike";
import { PET_ROUND, impactGate, shotKey } from "../statusFx";

const _spot = { x: 0, z: 0 };
const _mouth = new THREE.Vector3();
const _gate = new THREE.Vector3();
const _troop = new THREE.Vector3();
const _jaw = new THREE.Vector3();
/** The longest the sim's hit is held for a blow it struck before the pet could be staged (s). */
const PET_HOLD = 0.3;
/** Half a keep cell and a little: the fortress's walls reach the edge of its cells. */
const KEEP_HALF = 0.6;
const LOD_SWAP = 1.5;     // the least time between two changes of its body into its far LOD and back (s): no flicker
/** Where the bark's mote lands on the troop it mustered: this high above its feet (world units). */
const TROOP_CHEST = 0.15;

/**
 * The pet on the battlefield (docs/PETS.md): drawn only in a run that took one (`sim.mods.pet`). It sits a little
 * behind and to the side of the formation at the rally flag (petMind.ts petSpot), runs after the flag when it moves
 * (trotting a short shuffle; petHop.ts, its paws kept planted by the clip rate), faces the road the way the troops do,
 * and reacts to the tick's events: a bark when the fortress musters a troop (never more than once every
 * PET_TUNING.barkGap), a cheer on a cleared wave or the win, a hide when a boss arrives or a raider comes within reach,
 * a fidget now and then. How it is presented — its scale, the gold marker, the bark's shout and mote — is
 * petPresent.ts. Presentation only — its job is the sim's (sim/pet.ts).
 */
export class PetView {
  readonly group = new THREE.Group();
  private readonly petId: string | null;
  /** It barks at each muster (a pet whose passive musters troops: the pup). */
  private readonly musters: boolean;
  private body: PetBody | null = null;
  private marker: PetMarker | null = null;
  private readonly mind: PetMind;
  private readonly hop = new PetHop();
  private readonly look = petLook();
  private readonly reach = spotReach(this.look.scale);
  private time = 0;
  private lodAt = 0;     // not before this time does its body change between the model and its far LOD again
  private yaw = 0;
  private faceTo = 0;
  private act: PetAct | "" = "";
  private actStart = 0;
  /** The bark peaks (BARK_PEAKS) already answered for the bark playing. */
  private peaksDone = 0;
  private placed = false;
  /** The troop the fortress mustered last (the bark's mote flies to it). */
  private mustered = -1;
  /** What the bark playing is for: a troop mustered, or the pet's pulse (PetBarked). */
  private barkFor: "muster" | "pulse" = "muster";
  /** The sprite VFX, once loaded (level/levelScene.ts attachRouter). */
  fx: (() => PooledVfx | null) | null = null;
  private bark: PetBark | null = null;
  /** Each bark's first peak, with its mouth (world): the level's HUD answers it (level/levelPet.ts). */
  onBark: ((mouth: THREE.Vector3, why: "muster" | "pulse") => void) | null = null;
  /** Pets v2: Rally Bark's troop rings, Howl's shockwave and stars, the bite's spark (petAbilityFx.ts). */
  readonly abilities: PetAbilityFx;
  /** A clip of its own playing (bite, howl) until this time: the reactions wait, a move cuts it. */
  private ownUntil = 0;
  /** Its attack (petFight.ts): the run to the raider, the blow timed to the sim's, the stay, the way back. */
  private fight: PetFight | null = null;
  private readonly foes: PetFoes;
  /** The unit ground direction toward the camera (the fight comes at its raider from that side), and `at`'s answer, reused. */
  private readonly cam = { x: 0, z: 1 };
  private readonly where = { x: 0, z: 0 };
  /** The fight's pace for this body (petStrike.ts paceOf). */
  private pace: Pace = { speed: 1, accel: 1, walkUnder: 0.5 };
  /** The gallop its fight runs with (the tortoise's hurried trot) and that clip's measured gait. */
  private runClip: "run" | "hurry" = "run";
  private runGait: Gait = { cycle: 1, stride: 1 };

  /** The fortress's cells (its footprint), for what hides the pet. */
  private readonly keep: Cell[] = [];
  /** Whether world (x, z) is on the fortress (a keep cell, its walls to their edge): the fight never goes there. */
  private readonly inKeep = (x: number, z: number): boolean => {
    for (const k of this.keep) if (Math.abs(k.x - x) <= KEEP_HALF && Math.abs(k.y - z) <= KEEP_HALF) return true;
    return false;
  };
  /** The towers standing this tick (`hidden`), reused. */
  private readonly towers: Cell[] = [];

  /** `toCamera`: the unit ground direction (x, z) from the scene toward the camera, so the pet sits where it is seen. */
  constructor(private readonly sim: LevelSim, private readonly toCamera: (() => { readonly x: number; readonly z: number }) | null = null) {
    const g = sim.grid;
    for (let y = 0; y < g.height; y++) for (let x = 0; x < g.width; x++) { const c = new Cell(x, y); if (g.kindAt(c) === CellKind.Base) this.keep.push(c); }
    this.petId = sim.mods.pet?.id ?? null;
    this.musters = (sim.mods.pet?.spawnCutPercent ?? 0) > 0;
    this.mind = new PetMind(Math.random, 0);
    this.abilities = new PetAbilityFx(sim);
    this.foes = new PetFoes(sim, (id) => this.abilities.enemyAt(id));
    this.group.add(this.abilities.group);
    if (this.petId !== null) this.build(this.petId);
  }

  /** Where the pet stands now (world x / z), or null in a run without one. */
  get at(): { readonly x: number; readonly z: number } | null {
    if (!this.body) return null;
    const f = this.fight?.pose, w = this.where;
    if (f?.active) { w.x = f.x; w.z = f.z; } else { w.x = this.hop.x; w.z = this.hop.z; }
    return w;
  }

  private build(petId: string): void {
    this.body?.dispose();
    this.marker?.dispose();
    const b = new PetBody(petId, this.look.scale, undefined, EnemyViews.lodFar());
    b.fx = () => this.fx?.() ?? null;
    this.body = b;
    this.hop.configure(b.gaits, this.look.scale);
    this.group.add(b.root, b.shadow);
    this.marker = this.look.marker ? new PetMarker(this.look.scale) : null;
    if (this.marker) this.group.add(this.marker.mesh);
    this.bark = this.look.burst ? new PetBark(() => this.fx?.() ?? null, this.look.scale) : null;
    this.fight?.reset();
    const spec = strikeOf(petId), k = this.look.scale;
    // the tortoise's hurry when its model has it (measured), else the run
    const hurry = spec.runClip === "hurry" ? b.hurryGait : null;
    this.runGait = hurry ?? b.gaits.run;
    this.runClip = hurry ? "hurry" : "run";
    this.pace = paceOf(spec, naturalSpeed(this.runGait, k), naturalSpeed(b.gaits.walk, k), k);
    this.fight = new PetFight(spec, k, {
      // in the air the bird's own strike (the owl's swoop, the magpie's dive); on the ground the bite layered over the
      // stride (an act on a body without the layer)
      clip: (air) => {
        const own = air && spec.airClip && b.has(spec.airClip) ? spec.airClip : null;
        const len = own ? b.play(own) : b.strike("bite") || b.play("bite");
        this.ownUntil = this.time + len;
      },
      release: (id, flight) => this.abilities.spit(id, b.jaw(_jaw), flight),
      hit: (id, key) => { this.abilities.bit(id); if (key !== null) impactGate.land(key); },
      land: (key) => impactGate.land(key),
    }, this.pace);
    this.fight.setReach(this.foes.reach);
    this.fight.offLimits = this.inKeep;
  }

  /** Once per sim tick: the spot by the flag, the road's facing, the tick's events and any raider at its heels. */
  onTick(): void {
    const b = this.body, petId = this.petId;
    if (!b || petId === null) return;
    // the pet's own model arrived after the level was built (a resumed battle's pet that was not preloaded): wear it
    if (!b.hq && lib()?.has(`hq:${WornSkins.petModel(petId)}`)) this.build(petId);
    // past the far zoom (or back) it changes into its LOD (or back) while it has nothing of its own on
    else if (b.wantsSwap(EnemyViews.lodFar()) && this.time >= this.lodAt && !this.fight?.pose.active && this.time >= this.ownUntil) { this.build(petId); this.lodAt = this.time + LOD_SWAP; }
    const troops = this.sim.troops, rally = troops.rally, facing = headingOffset(troops.facing);
    const base = this.sim.grid.base, cam = this.toCamera?.() ?? null;
    if (cam) { this.cam.x = cam.x; this.cam.z = cam.z; }
    this.towers.length = 0;
    for (const t of this.sim.towerSlots) if (t.alive && t !== this.sim.baseTower) this.towers.push(t.cell);
    // never on the fortress (its gate cell included), a tower, rock or a hole, and where it can be seen if it can
    petSpot(rally, facing, rally.equals(base), _spot, (cx, cy) => !(cx === base.x && cy === base.y) && troops.walkable(new Cell(cx, cy)), (cx, cy) => !this.hidden(cx, cy, cam), this.reach);
    if (!this.placed) { this.hop.place(_spot.x, _spot.z); this.placed = true; }
    else {
      const t = this.hop.target;
      if (Math.abs(t.x - _spot.x) > 1e-6 || Math.abs(t.z - _spot.z) > 1e-6) {
        // the way a troop would walk there, cell by cell (round towers and the fortress, never over a cliff)
        const cells = troops.pathFrom(new Cell(Math.round(this.hop.x), Math.round(this.hop.z)), new Cell(Math.round(_spot.x), Math.round(_spot.z)));
        this.hop.go(_spot.x, _spot.z, cells.slice(0, -1).map((c) => ({ x: c.x, z: c.y })), this.yaw);
      }
    }
    this.faceTo = faceYaw(facing);
    const now = this.time, events = this.sim.events.all();
    for (const ev of events) {
      // the fortress's muster (not a Barracks'), barked at by a pet that musters them (the pup's passive)
      if (ev.kind === SimEventKind.TroopSpawned) { if (this.musters && troops.find(ev.a)?.ownerTowerId === 0) { this.mustered = ev.a; this.barkFor = "muster"; this.mind.ask("bark", now); } }
      else if (ev.kind === SimEventKind.StageCleared || ev.kind === SimEventKind.LevelWon) this.mind.ask("cheer", now);
      else if (ev.kind === SimEventKind.BossSpawned) this.mind.ask("hide", now);
      else if (ev.kind === SimEventKind.PetBit) this.bite(ev.a);
      else if (ev.kind === SimEventKind.PetBarked) { this.barkFor = "pulse"; this.mind.ask("bark", now); this.abilities.barked(); }
      else if (ev.kind === SimEventKind.PetMended) this.abilities.mended(ev.a);
      else if (ev.kind === SimEventKind.PetHowled) this.howl(events, ev);
    }
    const fight = this.fight;
    if (fight) fight.foresee(now, this.sim.tick, this.foes.read(), this.hop, this.foes.at, this.cam, this.foes.near);
    // it hides from a raider at its heels, not from the one it is fighting
    for (const e of this.sim.enemySlots) {
      if (fight?.pose.active) break;
      if (!e.alive || e.burrowed) continue;
      if (withinReach(this.hop.x, this.hop.z, e.x.toNumber(), e.y.toNumber())) { this.mind.ask("hide", now); break; }
    }
  }

  /**
   * Whether cell (cx, cy) is a poor place to be seen: the fortress or a tower between it and the camera (walls, keep,
   * a tower's roof and posts hide what stands behind them, a tall tower well beyond its own cell; without a camera,
   * any cell touching the fortress), or the
   * map's outer ring (the scenery past the edge — trees, cliffs — stands in front of it).
   */
  private hidden(cx: number, cy: number, cam: { readonly x: number; readonly z: number } | null): boolean {
    const g = this.sim.grid;
    if (cx <= 0 || cy <= 0 || cx >= g.width - 1 || cy >= g.height - 1) return true;
    if (cam) return keepHides(cx, cy, this.keep, cam) || towerHides(cx, cy, this.towers, cam);
    return this.keep.some((k) => Math.max(Math.abs(k.x - cx), Math.abs(k.y - cy)) <= 1);
  }

  /**
   * The attack (PetBit) on raider `enemyId`: the fight runs to it (or spits) and lands the blow; the sim's hit, held for
   * it (statusFx.ts PET_ROUND), shows when it lands, by its deadline at the latest.
   */
  private bite(enemyId: number): void {
    const f = this.fight, key = shotKey(this.sim.tick, PET_ROUND);
    if (!f) return;
    f.struck(this.time, enemyId, key, this.hop, this.foes.at, this.cam);
    // a blow staged ahead lands now or within a frame; one the sim struck unseen is held at most PET_HOLD (the HP bar
    // must not trail the sim visibly), so a long run shows the hit a little before the pet gets there
    const due = f.hitAt;
    if (due !== null) impactGate.expect(key, Math.min(due - this.time, PET_HOLD));
  }

  /** Howl (PetHowled): the clip, the shockwave, the stars over the raiders it stunned (this event's EnemyStunned, b = 0). */
  private howl(events: readonly { kind: SimEventKind; a: number; b: number }[], _ev: unknown): void {
    const b = this.body;
    if (!b) return;
    const ids = events.filter((e) => e.kind === SimEventKind.EnemyStunned && e.b === 0).map((e) => e.a);
    const at = this.at ?? this.hop;
    this.abilities.howled(at.x, at.z, ids);
    if (this.hop.moving || this.fight?.striking) return;
    this.ownUntil = this.time + b.play("howl");
  }

  /** Every frame: the move (or the fight), the turn toward its way, the reaction to start, the pose. */
  interpolate(dt: number): void {
    const b = this.body;
    if (!b) return;
    this.time += dt;
    const now = this.time;
    this.hop.step(dt);
    const F = this.foes, f = this.fight?.update(now, dt, this.hop, F.at, this.cam, F.interval, F.obstacles, F.count) ?? null;
    const fighting = !!f?.active;
    const moving = this.hop.moving || fighting;
    const own = now < this.ownUntil;
    if (this.hop.moving && own && !fighting) { b.endAct(); this.ownUntil = 0; }
    const next = own || moving ? null : this.mind.next(now, moving);
    if (moving) this.mind.next(now, true);                                // a fidget wanted gives way; the rest wait
    if (next !== null) { this.mind.started(next, b.play(next), now); this.act = next; this.actStart = now; this.peaksDone = 0; }
    if (this.mind.playing(now) === "") this.act = "";
    if (moving && this.act !== "") { b.endAct(); this.act = ""; }      // a move cuts whatever it was doing (it blends out)
    if (this.act === "bark") this.barkPeaks(now - this.actStart);
    const x = fighting && f ? f.x : this.hop.x, z = fighting && f ? f.z : this.hop.z;
    const want = fighting && f ? f.yaw ?? this.yaw : this.hop.moving ? this.hop.heading : this.faceTo;
    this.yaw = turnToward(this.yaw, want, fighting ? PET_STRIKE_TUNING.turnRate : PET_MOVE.turnRate, dt);
    b.root.position.set(x, 0, z);
    const ground = GroundMesh.drawnAt(x, z);
    const gait = this.hop.gait;
    // the gait while it covers ground: the fight's from its speed and height (run, walk, idle; fly, glide, hover),
    // the hop's past its turn on the spot; idle on the spot
    const fg = fighting && f ? gaitOf(f.speed, f.lift, f.climb, this.pace.walkUnder) : null;
    const loop = fg === "run" ? this.runClip : fg ?? (gait && this.hop.setOff && this.hop.left > PET_MOVE.fade / 2 ? gait : "idle");
    // in a fight the stride follows the speed all the way (the tortoise's scurry runs its clip fast): the paws stay planted
    const g = fg === "run" ? this.runGait : fg === "walk" ? b.gaits.walk : null;
    const rate = fighting && f ? (g ? Math.max(PET_MOVE.rate[0], f.speed / naturalSpeed(g, this.look.scale)) : 1) : this.hop.rate;
    b.pose(dt, { loop, rate, yaw: this.yaw, ground: ground + (f?.active ? f.lift : 0), act: this.act || loop, actT: this.act ? now - this.actStart : now });
    this.marker?.update(dt, x, ground, z);
    this.abilities.update(dt);
  }

  /** The bark's two peaks as they come: the shout (and on the first, the mote and the HUD's answer), the marker's flare. */
  private barkPeaks(t: number): void {
    const b = this.body;
    while (b && this.peaksDone < BARK_PEAKS.length && t >= BARK_PEAKS[this.peaksDone]) {
      const first = this.peaksDone === 0;
      this.peaksDone++;
      b.mouth(_mouth);
      const gate = this.sim.grid.base;
      _gate.set(gate.x, 0, gate.y);
      const id = this.mustered, sim = this.sim;
      const t = id >= 0 ? sim.troops.find(id) : undefined;
      const troop = t && t.alive ? _troop.set(t.x.toNumber(), GroundMesh.drawnAt(t.x.toNumber(), t.y.toNumber()) + TROOP_CHEST, t.y.toNumber()) : null;
      this.marker?.flare();
      if (first) this.onBark?.(_mouth, this.barkFor);
      this.bark?.bark(_mouth, _gate, first, troop);
    }
  }

  /** A resumed battle's first frame (after the scene's catch-up tick, which stood it on its spot): pose it. */
  catchUp(): void { this.interpolate(0); }

  dispose(): void {
    this.fight?.reset();
    this.body?.dispose();
    this.marker?.dispose();
    this.abilities.dispose();
    this.body = null; this.marker = null;
    this.group.clear();
  }
}
